// ModernWidthPresets — 太さプリセットボタン列 (§9.11 太さプリセット、consult 2026-09-29)。
//
// **明示登録型** (Procreate/MediBang 型、決定1): ボタンはクリックで太さを**適用する
// だけ**、スライダ操作では登録値を変えない (= 上書きしない、Classic の 6 cell と違う)。
// 現在値と一致するボタンだけ点灯する。登録は見える「+」ボタンで現在値を追加する
// (決定2、長押し/右クリック登録は発見性が低いので不採用)。
//
// 削除 (§9.11 細目):
//   - マウス/キーボード: ホバー (or focus、キーボード到達性のため) で現れる × を押すと
//     **即削除** (低リスクな UI 設定の取り消しなので確認なし。「+」ですぐ登録し直せる)。
//   - タッチ (ホバー無し): 長押しで `ConfirmDialog` の削除確認を出す (誤操作防止、
//     細目「タッチ環境は長押しで削除メニュー (または確認付き削除)」の後者を採用)。
// 最後の 1 個 (`MIN_WIDTH_PRESETS`) は削除できない (= 空にすると復帰手段が無い)。
// このときは × / 長押し削除自体を出さない (誤操作の的にしない)。
//
// RESET (2026-09-29 追加、カラーパレット「初期色に戻す」と同じ FB 系統): 行の右端に
// 小さなアイコン (`ResetIcon`) で置き、縦に行を増やさない (= ツールプロパティを縦に膨らませない)。消した既定値の
// 復帰手段。複数の登録を一度に失うので、パレットと同じく `ConfirmDialog` で確認する。
// 既に既定と同じなら disabled。
//
// ## タッチ長押し × ConfirmDialog のゴーストクリック (oekaki/CLAUDE.md 既知の罠)
//
// 長押しタイマーは `pointerdown` から `setTimeout` で発火させる (= 指がまだ画面上に
// ある間に `ConfirmDialog` が開く)。この直後の `pointerup` は「押下時には存在しなかった
// 新出現の backdrop」に対して touch 由来の互換 click を送るため、素朴に実装すると
// 即座に閉じてしまう (CLAUDE.md「タッチのゴーストクリックはオーバーレイを即閉じる」と
// 同型の罠)。`ConfirmDialog` は **open 時に「ダイアログ内で pointerdown があったか」で
// arm するガードを内蔵**しており、このケース (= ダイアログの外・開く前から進行中だった
// pointerdown 由来の click) を自動的に弾く。呼び出し側 (本コンポーネント) は無改修で
// 守られるので、追加のガードは書かない (ConfirmDialog.tsx 冒頭コメント参照)。

import { useEffect, useRef, useState } from 'react';

import { ConfirmDialog } from '../../ConfirmDialog.js';
import { ResetIcon } from './ModernIcons.js';
import { isDefaultWidthPresets, MAX_WIDTH_PRESETS, MIN_WIDTH_PRESETS } from './widthPresets.js';

/** タッチ長押しで削除確認を出すまでの時間 (ms、実機調整余地あり)。 */
const LONG_PRESS_MS = 550;

/**
 * ドット表示の直径 (px)。太さをそのまま使うと小さい値が見えず・大きい値がボタンから
 * はみ出すので、視認できる範囲 (4..20px) に圧縮してマッピングする (見た目の相対感が
 * 分かればよく、正確な px 一致は要らない)。
 */
const dotDiameterPx = (width: number): number =>
  Math.min(20, Math.max(4, Math.round(width / 3) + 4));

export interface ModernWidthPresetsProps {
  /** 登録済みプリセット (正規化済み: 昇順・重複無し・最大 `MAX_WIDTH_PRESETS` 個)。 */
  readonly presets: readonly number[];
  /** 現在の太さ (アクティブバケットの実効値、点灯判定に使う)。 */
  readonly currentWidth: number;
  /** プリセットをクリック → その太さを適用するだけ (登録値は変えない、決定1)。 */
  readonly onSelect: (width: number) => void;
  /** 「+」→ 現在の太さを登録 (決定2)。 */
  readonly onAdd: () => void;
  /** プリセットを削除。 */
  readonly onRemove: (width: number) => void;
  /** 既定 (`DEFAULT_WIDTH_PRESETS`) に戻す。 */
  readonly onReset: () => void;
}

/**
 * 太さプリセットボタン列。`ModernToolProperties` 自体は hook を持たない設計
 * (call-direct テスト様式のため) だが、本コンポーネントは長押し/確認ダイアログの
 * ローカル state を持つ必要があるため `BrushSamplePreview` と同じく**別の子コンポーネント**
 * として hooks を使う (= 親を hook レスに保ったまま機能を足せる、既存分離パターン)。
 */
export const ModernWidthPresets = ({
  presets,
  currentWidth,
  onSelect,
  onAdd,
  onRemove,
  onReset,
}: ModernWidthPresetsProps): React.ReactNode => {
  // タッチ長押しで削除確認する対象の太さ (null = 非表示)。
  const [confirmTarget, setConfirmTarget] = useState<number | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 直前の押下が長押し発火済みか (= 続く click を選択に流さないためのフラグ)。
  const longPressFiredRef = useRef(false);

  // アンマウント時にタイマーを残さない (= 破棄後の setState を防ぐ)。
  useEffect(
    () => () => {
      if (longPressTimerRef.current !== null) clearTimeout(longPressTimerRef.current);
    },
    [],
  );

  const clearLongPressTimer = (): void => {
    if (longPressTimerRef.current !== null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const canDelete = presets.length > MIN_WIDTH_PRESETS;
  const maxReached = presets.length >= MAX_WIDTH_PRESETS;

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>, width: number): void => {
    // ホバーが効くマウスは既存の × で足りる (= 長押しはタッチ/ペン限定)。
    if (e.pointerType === 'mouse') return;
    if (!canDelete) return;
    longPressFiredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setConfirmTarget(width);
    }, LONG_PRESS_MS);
  };

  const handlePointerRelease = (): void => {
    clearLongPressTimer();
  };

  const handleClick = (width: number): void => {
    // 長押しが発火した押下から続く click (= 同じタッチシーケンスの終端) は選択扱いに
    // しない (長押しは削除確認を開く操作であって、太さ適用ではない)。
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onSelect(width);
  };

  return (
    <>
      <fieldset className="m-0 flex items-center gap-1 border-0 p-0">
        <legend className="sr-only">太さプリセット</legend>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {presets.map((w) => {
            const active = Math.round(currentWidth) === w;
            return (
              <span key={w} className="group relative inline-flex">
                <button
                  type="button"
                  onClick={() => handleClick(w)}
                  onPointerDown={(e) => handlePointerDown(e, w)}
                  onPointerUp={handlePointerRelease}
                  onPointerCancel={handlePointerRelease}
                  onPointerLeave={handlePointerRelease}
                  onContextMenu={(e) => e.preventDefault()}
                  aria-pressed={active}
                  aria-label={`太さ ${w}px${active ? ' (現在の太さ)' : ''}`}
                  title={`${w}px`}
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border transition-colors ${
                    active
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)]'
                      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] hover:bg-[var(--color-bg)]'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`rounded-full ${active ? 'bg-black' : 'bg-white'}`}
                    style={{ width: dotDiameterPx(w), height: dotDiameterPx(w) }}
                  />
                </button>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => onRemove(w)}
                    aria-label={`太さ ${w}px のプリセットを削除`}
                    className="absolute -right-1.5 -top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] text-[8px] leading-none text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })}
          <button
            type="button"
            onClick={onAdd}
            disabled={maxReached}
            aria-label="現在の太さをプリセットに追加"
            title={
              maxReached
                ? `これ以上追加できません (最大 ${MAX_WIDTH_PRESETS} 個)`
                : '現在の太さをプリセットに追加'
            }
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] text-[12px] font-bold text-white hover:bg-[var(--color-bg)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            +
          </button>
        </div>
        <button
          type="button"
          onClick={() => setResetConfirmOpen(true)}
          disabled={isDefaultWidthPresets(presets)}
          aria-label="太さプリセットを初期値に戻す"
          title="太さプリセットを初期値に戻す"
          className="flex h-5 w-5 shrink-0 items-center justify-center self-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)] hover:text-white disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[var(--color-text-muted)]"
        >
          <ResetIcon className="h-3 w-3" />
        </button>
      </fieldset>
      <ConfirmDialog
        open={confirmTarget !== null}
        title="プリセットを削除"
        message={
          confirmTarget === null ? '' : `太さ ${confirmTarget}px のプリセットを削除しますか？`
        }
        confirmLabel="削除"
        cancelLabel="キャンセル"
        destructive
        onConfirm={() => {
          if (confirmTarget !== null) onRemove(confirmTarget);
          setConfirmTarget(null);
        }}
        onCancel={() => setConfirmTarget(null)}
      />
      <ConfirmDialog
        open={resetConfirmOpen}
        title="太さプリセットをリセット"
        message={'太さプリセットを初期値に戻しますか?\n登録した太さは失われます。'}
        confirmLabel="リセット"
        destructive
        onConfirm={() => {
          onReset();
          setResetConfirmOpen(false);
        }}
        onCancel={() => setResetConfirmOpen(false)}
      />
    </>
  );
};
